'use client';

import React from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '@liftango/ui-icons';
import { MonoValue } from '../MonoValue/MonoValue';

export type EventTone = 'neutral' | 'info' | 'success' | 'warn' | 'error' | 'brand';

export interface TimelineEvent {
  /** ISO 8601 timestamp. */
  at: string;
  /** Bold first line — what happened. */
  label: React.ReactNode;
  /** Optional second line — extra context. */
  detail?: React.ReactNode;
  /** Optional third line — who or what triggered the event (system / driver / agent.x). */
  actor?: React.ReactNode;
  /** Optional leading icon node (use `Icon` from `@liftango/ui-icons`). Renders inside the dot. */
  icon?: React.ReactNode;
  /** Tone — colours the dot rail. */
  tone: EventTone;
  /**
   * Full bullet replacement. When set, the default tone-coloured `Dot` and
   * its `icon` are skipped for this row and `bullet` renders directly on
   * the rail in their place. Use to drop in a richer marker such as a
   * `StopChip` or `StopPin`. The connector line still renders below.
   *
   * Set `railWidth` on the parent to give the column enough room for the
   * widest bullet — otherwise wide nodes will overflow the rail track.
   */
  bullet?: React.ReactNode;
  /** Ref to the row's outer element. Use for imperative work like scroll-into-view. */
  rowRef?: React.Ref<HTMLDivElement>;
  /** Right-click handler for the entire row. */
  onContextMenu?: (e: React.MouseEvent) => void;
}

/**
 * A named subset of timeline events with an optional collapse summary.
 * Use to bucket events by lifecycle phase, severity, lane, or any other
 * partition. Groups render inline in the same continuous rail — no box
 * chrome, no per-group reset. A small header sits above each group with
 * a chevron toggle, the label, and a summary slot.
 *
 * Pass `groups` to `EventTimeline` to render the bucketed view. Pass
 * `events` for a flat (un-bucketed) timeline.
 */
export interface TimelineGroup {
  /** Stable id used for React keys and `data-testid` selectors. */
  id: string;
  /** Section heading. Plain string or a richer node. */
  label: React.ReactNode;
  /** Events inside this group, in display order. */
  events: TimelineEvent[];
  /**
   * Whether the group starts open. When `undefined`, defaults to `true`.
   * Pass `false` to render a collapsed summary that the user can click
   * to expand.
   */
  defaultOpen?: boolean;
  /**
   * Optional collapsed-state summary node. When omitted, defaults to a
   * compact strip of tone-coloured mini-dots — one per contained event
   * — so a collapsed group still hints at the number and tone of its
   * contents. Pass any ReactNode (count chip, plate, custom strip) to
   * replace the default.
   */
  summary?: React.ReactNode;
  /**
   * Empty-state label shown inside the group body when `events.length`
   * is 0 AND the group is open. Falls back to the parent
   * `EventTimelineProps.emptyLabel`.
   */
  emptyLabel?: React.ReactNode;
}

export interface EventTimelineProps {
  /** Flat event list. Required unless `groups` is supplied. */
  events?: TimelineEvent[];
  /**
   * Grouped event list. When supplied, takes precedence over `events`
   * and renders one `<details>` per group with the events inside. Each
   * group manages its own connector rail and date breaks.
   */
  groups?: TimelineGroup[];
  /** Override the empty-state label. Defaults to `"No events recorded."`. */
  emptyLabel?: string;
  /** Time zone for date/time formatting. Defaults to `UTC`. */
  timeZone?: string;
  /**
   * Width of the rail column. Defaults to `24px` — sized for the standard
   * 22 px tone-dot. Widen (e.g. `96`) when any row supplies a `bullet`
   * override that exceeds the default disc (chips, pills, larger markers).
   * Accepts a number (treated as px) or any CSS length string. Only
   * applied in the `'rail'` layout.
   */
  railWidth?: number | string;
  /**
   * Visual layout of each row.
   *
   * - `'rail'` (default) — three columns: time | tone-dot or bullet | body.
   *   Body sits to the right of the rail; appropriate when every row's
   *   marker is a small disc.
   * - `'nested'` — two columns: time | track. The bullet sits at the top
   *   of the track, left-aligned, with the connector stem dropping down
   *   from the bullet's leading-disc centre and the body content
   *   indenting underneath. Use when bullets are wide pills (e.g.
   *   `StopChip`) and the natural reading order is bullet-then-content.
   */
  layout?: 'rail' | 'nested';
  /**
   * Only applies in `'nested'` layout. Distance from the track's left
   * edge to the connector stem in px. Should align with the centre of
   * the bullet's leading disc. Defaults to `13` — the disc centre of a
   * `StopChip` with its standard inner padding.
   */
  stemOffset?: number;
  /**
   * Only applies in `'nested'` layout. Distance from the stem to the
   * left edge of the body content in px. Defaults to `12` — tight enough
   * for the body to read as "indented under" the bullet, loose enough to
   * leave the stem visible.
   */
  bodyIndent?: number;
  className?: string;
}

const formatTime = (iso: string, timeZone: string) => {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone });
};

const formatDate = (iso: string, timeZone: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone });
};

/**
 * Generic vertical timeline of events. Each event has a tone-coloured dot
 * on a continuous rail, a mono time on the left, and a label / optional
 * detail / optional actor on the right.
 *
 * Use for audit trails, status histories, status-change feeds, and any
 * chronological event display. For *trip stops* (pickup / dropoff with
 * activity colours) use `RouteTimeline` from `@liftango/ui-ops` — that's
 * a different visual language tied to the schedule legend.
 *
 * Caller order is preserved as-is; this component does not sort. Sort
 * upstream so the data layer is the source of truth.
 */
export function EventTimeline({
  events,
  groups,
  emptyLabel = 'No events recorded.',
  timeZone = 'UTC',
  railWidth = 24,
  layout = 'rail',
  stemOffset = 13,
  bodyIndent = 12,
  className,
}: EventTimelineProps) {
  const railSize = typeof railWidth === 'number' ? `${railWidth}px` : railWidth;

  // Grouped path: one continuous List for the whole timeline. Each
  // group emits a thin <details> header row (chevron + label + summary
  // slot) directly into the list, followed by its event rows when open.
  // No box chrome — the rail flows uninterrupted across groups.
  if (groups) {
    return (
      <List className={className}>
        {groups.map((g, gi) => {
          const open = g.defaultOpen !== false;
          const summary = g.summary ?? <DefaultStackedSummary events={g.events} />;
          // Every group except the last forces a trailing connector on
          // its last row so the rail visually flows into the next
          // group's header — preserves "one continuous timeline".
          const isLastGroup = gi === groups.length - 1;
          return (
            <GroupSection key={g.id} open={open} data-testid={`timeline-group-${g.id}`}>
              <GroupSummary $railWidth={railSize}>
                <GroupTimeSlot />
                {/* Bullet sits on the rail. When collapsed it carries the
                    summary node (default = a strip of mini-dots). When
                    open it's blanked so the connector visually flows
                    past the header into the first event below. */}
                <GroupBullet data-open={open ? 'true' : 'false'}>{!open && summary}</GroupBullet>
                <GroupBody>
                  <GroupLabel>{g.label}</GroupLabel>
                  <GroupCount>{g.events.length}</GroupCount>
                  <GroupChevron name={open ? 'chevron-up' : 'chevron-down'} size={11} />
                </GroupBody>
              </GroupSummary>
              {g.events.length === 0 ? (
                <Empty>{g.emptyLabel ?? emptyLabel}</Empty>
              ) : (
                renderRows(g.events, {
                  timeZone,
                  railSize,
                  layout,
                  stemOffset,
                  bodyIndent,
                  forceTrailingConnector: !isLastGroup,
                })
              )}
            </GroupSection>
          );
        })}
      </List>
    );
  }

  // Flat path — original contract preserved.
  if (!events || events.length === 0) {
    return <Empty className={className}>{emptyLabel}</Empty>;
  }
  return (
    <List className={className}>
      {renderRows(events, { timeZone, railSize, layout, stemOffset, bodyIndent })}
    </List>
  );
}

/**
 * Walk a single list of events into rendered row nodes.
 *
 * `forceTrailingConnector` — when true, the LAST row in this slice
 * still emits its trailing connector. Used by the grouped path for
 * every group except the last so the rail flows visually through
 * group boundaries instead of breaking at each.
 */
function renderRows(
  events: TimelineEvent[],
  opts: {
    timeZone: string;
    railSize: string;
    layout: 'rail' | 'nested';
    stemOffset: number;
    bodyIndent: number;
    forceTrailingConnector?: boolean;
  },
): React.ReactNode[] {
  const { timeZone, railSize, layout, stemOffset, bodyIndent, forceTrailingConnector } = opts;
  let lastDate = '';
  return events.map((e, i) => {
    const date = formatDate(e.at, timeZone);
    const showDateBreak = date !== lastDate;
    lastDate = date;
    const isLast = !forceTrailingConnector && i === events.length - 1;

    if (layout === 'nested') {
      return (
        <NestedRow key={i} ref={e.rowRef} onContextMenu={e.onContextMenu}>
          <TimeCol>
            {showDateBreak && <DateLabel>{date}</DateLabel>}
            <MonoValue value={formatTime(e.at, timeZone)} />
          </TimeCol>
          <NestedTrack>
            <NestedBullet>
              {e.bullet !== undefined ? e.bullet : <Dot $tone={e.tone}>{e.icon}</Dot>}
            </NestedBullet>
            <NestedLower $stemOffset={stemOffset} $bodyIndent={bodyIndent} $isLast={isLast}>
              {!isLast && <NestedStem $stemOffset={stemOffset} data-testid="timeline-connector" />}
              <NestedBody>
                <Label data-testid="timeline-label">{e.label}</Label>
                {e.detail !== undefined && e.detail !== null && <Detail>{e.detail}</Detail>}
                {e.actor !== undefined && e.actor !== null && (
                  <Actor>
                    <MonoValue value={e.actor as string} muted />
                  </Actor>
                )}
              </NestedBody>
            </NestedLower>
          </NestedTrack>
        </NestedRow>
      );
    }

    return (
      <Row key={i} $railWidth={railSize} ref={e.rowRef} onContextMenu={e.onContextMenu}>
        <TimeCol>
          {showDateBreak && <DateLabel>{date}</DateLabel>}
          <MonoValue value={formatTime(e.at, timeZone)} />
        </TimeCol>
        <RailCol>
          {e.bullet !== undefined ? (
            <BulletSlot>{e.bullet}</BulletSlot>
          ) : (
            <Dot $tone={e.tone}>{e.icon}</Dot>
          )}
          {!isLast && <Connector data-testid="timeline-connector" />}
        </RailCol>
        <BodyCol>
          <Label data-testid="timeline-label">{e.label}</Label>
          {e.detail !== undefined && e.detail !== null && <Detail>{e.detail}</Detail>}
          {e.actor !== undefined && e.actor !== null && (
            <Actor>
              <MonoValue value={e.actor as string} muted />
            </Actor>
          )}
        </BodyCol>
      </Row>
    );
  });
}

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div<{ $railWidth: string }>`
  display: grid;
  grid-template-columns: 88px ${(p) => p.$railWidth} 1fr;
  align-items: flex-start;
  gap: ${(p) => p.theme.spaceAlias.sm};
  /* Pin a baseline row height so the connector segment between dots is
     visually uniform regardless of how many body lines the event carries
     (label / detail / actor). Rows with extra-long detail content can
     still grow past this floor. */
  min-height: 64px;
  padding-bottom: ${(p) => p.theme.spaceAlias.md};

  &:last-child {
    min-height: 0;
    padding-bottom: 0;
  }
`;

const TimeCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  padding-top: 2px;
`;

const DateLabel = styled.div`
  font-size: ${(p) => p.theme.fontSize.eyebrow};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.neutral.inkSoft};
`;

const RailCol = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
`;

const toneStyles = {
  neutral: css`
    background: ${(p) => p.theme.colors.neutral.panelAlt};
    color: ${(p) => p.theme.colors.neutral.inkSoft};
    border-color: ${(p) => p.theme.colors.neutral.line};
  `,
  info: css`
    background: ${(p) => p.theme.colors.semantic.info.soft};
    color: ${(p) => p.theme.colors.semantic.info.color};
    border-color: ${(p) => p.theme.colors.semantic.info.color};
  `,
  success: css`
    background: ${(p) => p.theme.colors.semantic.success.soft};
    color: ${(p) => p.theme.colors.semantic.success.color};
    border-color: ${(p) => p.theme.colors.semantic.success.color};
  `,
  warn: css`
    background: ${(p) => p.theme.colors.semantic.warn.soft};
    color: ${(p) => p.theme.colors.semantic.warn.color};
    border-color: ${(p) => p.theme.colors.semantic.warn.color};
  `,
  error: css`
    background: ${(p) => p.theme.colors.semantic.error.soft};
    color: ${(p) => p.theme.colors.semantic.error.color};
    border-color: ${(p) => p.theme.colors.semantic.error.color};
  `,
  brand: css`
    background: ${(p) => p.theme.colors.brand.primarySoft};
    color: ${(p) => p.theme.colors.brand.primary};
    border-color: ${(p) => p.theme.colors.brand.primary};
  `,
};

const Dot = styled.div<{ $tone: EventTone }>`
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  z-index: 1;
  ${(p) => toneStyles[p.$tone]}
`;

/** Wrapper for a custom `bullet` override — no tone styling, just a
 *  centred slot that sits above the connector so the connector visually
 *  emerges from the bullet's centre regardless of bullet shape. */
const BulletSlot = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 1;
`;

const Connector = styled.div`
  flex: 1;
  width: 2px;
  background: ${(p) => p.theme.colors.neutral.line};
  margin-top: 2px;
`;

const BodyCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 2px;
`;

const Label = styled.div`
  font-size: ${(p) => p.theme.fontSize.sm};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  color: ${(p) => p.theme.colors.neutral.ink};
`;

const Detail = styled.div`
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
`;

const Actor = styled.div`
  font-size: ${(p) => p.theme.fontSize.xs};
`;

const Empty = styled.div`
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.inkMute};
  font-style: italic;
`;

// ── Nested layout ────────────────────────────────────────────────────────
// Two-column row: time on the left, then a single "track" column that
// stacks the bullet over an indented body. The connector stem drops from
// the bullet's leading-disc centre down through the body content into the
// next row's bullet.

const NestedRow = styled.div`
  display: grid;
  grid-template-columns: 88px 1fr;
  align-items: flex-start;
  gap: ${(p) => p.theme.spaceAlias.sm};
`;

const NestedTrack = styled.div`
  display: flex;
  flex-direction: column;
`;

const NestedBullet = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
`;

const NestedLower = styled.div<{ $stemOffset: number; $bodyIndent: number; $isLast: boolean }>`
  position: relative;
  padding-left: ${(p) => p.$stemOffset + p.$bodyIndent}px;
  /* Top padding lets the connector "breathe" out of the bullet before
     the body content begins reading — without it the address line crowds
     the chip. Bottom padding builds the inter-row rhythm. */
  padding-top: ${(p) => p.theme.spaceAlias.md};
  padding-bottom: ${(p) => (p.$isLast ? 0 : p.theme.spaceAlias.lg)};
`;

const NestedStem = styled.div<{ $stemOffset: number }>`
  position: absolute;
  left: ${(p) => p.$stemOffset - 1}px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: ${(p) => p.theme.colors.neutral.line};
`;

const NestedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// ── Group layout ─────────────────────────────────────────────────────────
// Each group is a `<details>` that emits a thin header row into the same
// continuous List as a flat timeline — no box chrome, no rail break.
// The header mirrors a normal Row grid: blank time slot, a bullet on
// the rail, then label + count + chevron in the body.
//
// When the group is COLLAPSED the bullet carries the summary node (by
// default a strip of tone-coloured mini-dots, one per hidden event) so
// the rail visually communicates "n stops live here". When OPEN the
// bullet is blanked and the events render below the header so the rail
// connector flows uninterrupted into the first event's dot.

const GroupSection = styled.details`
  display: contents;
`;

const GroupSummary = styled.summary<{ $railWidth: string }>`
  /* Mirror Row's grid — keeps the header bullet aligned with every
     event dot on the rail. */
  display: grid;
  grid-template-columns: 88px ${(p) => p.$railWidth} 1fr;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
  padding: ${(p) => p.theme.spaceAlias.xs} 0;
  cursor: pointer;
  user-select: none;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }
`;

/** Blank cell so the bullet column-aligns with event dots. The time
 *  column is intentionally empty on group headers — the times come
 *  from the events themselves. */
const GroupTimeSlot = styled.div``;

/** Rail-column slot for the group header. When collapsed it hosts the
 *  consumer's `summary` (or the default mini-dot strip); when open it
 *  collapses to a thin connector segment so the rail flows through. */
const GroupBullet = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;

  /* When open, render a 2px connector down the middle so the rail line
     visually continues into the first event's dot below. */
  &[data-open='true']::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${(p) => p.theme.colors.neutral.line};
  }
`;

const GroupBody = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${(p) => p.theme.spaceAlias.sm};
`;

const GroupLabel = styled.span`
  font-size: ${(p) => p.theme.fontSize.eyebrow};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.neutral.inkSoft};
`;

const GroupCount = styled.span`
  font-family: ${(p) => p.theme.fontFamily.mono};
  font-size: ${(p) => p.theme.fontSize.xs};
  color: ${(p) => p.theme.colors.neutral.inkMute};
`;

const GroupChevron = styled(Icon)`
  color: ${(p) => p.theme.colors.neutral.inkMute};
`;

/**
 * Default collapsed-state summary — a full-size 22px tone-coloured dot
 * (matching every event dot on the rail, carrying the tone of the first
 * event in the bucket) with an `ellipsis` glyph centred inside to imply
 * "more behind here". Consumers can replace via `TimelineGroup.summary`.
 */
function DefaultStackedSummary({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) return null;
  return (
    <Dot
      $tone={events[0].tone}
      aria-label={`${events.length} event${events.length === 1 ? '' : 's'}`}
    >
      <Icon name="ellipsis" size={10} />
    </Dot>
  );
}
